import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { BarChart3, ArrowLeft } from "lucide-react";
import { PUBLIC_BUSINESS } from "@/lib/public-business";

export default function TermsPage() {
  const { t } = useTranslation(["auth"]);
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-background sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-bold text-lg">DealerMind</span>
          </div>
          <Link
            href="/login"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="link-back-to-login"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("auth:pageChrome.backToLogin")}
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-sm dark:prose-invert max-w-none">
          <h1 className="text-3xl font-bold mb-1">Terms of Service</h1>
          <p className="text-muted-foreground text-sm mb-10">Last updated: June 2026</p>

          <p className="text-muted-foreground leading-relaxed mb-8">
            Please read these Terms of Service ("Terms") carefully before using the DealerMind platform ("Service," "Platform")
            operated by DealerMind Intelligence LLC ("DealerMind," "we," "us," or "our"). By creating an account or using the Service
            you agree to be bound by these Terms. If you do not agree, do not use the Service.
          </p>

          <Section title="1. Eligibility and Accounts">
            <Clause title="1.1 Business Use.">
              The Service is intended solely for use by businesses and business professionals. By registering you represent
              that you are at least 18 years old and are using the Service on behalf of a legal business entity.
            </Clause>
            <Clause title="1.2 Account Registration.">
              You must provide accurate, complete, and up-to-date information when creating an account. You are responsible
              for maintaining the confidentiality of your login credentials and for all activity that occurs under your account.
            </Clause>
            <Clause title="1.3 One Account per Dealership.">
              Each dealership may maintain one primary account. Admin users may add staff sub-accounts under that account.
            </Clause>
            <Clause title="1.4 Account Security.">
              You must notify us immediately at {PUBLIC_BUSINESS.email} if you suspect unauthorized access to your account. We are
              not liable for any loss resulting from unauthorized use of your credentials.
            </Clause>
          </Section>

          <Section title="2. Subscription and Billing">
            <Clause title="2.1 Paid Subscription.">
              Access to the Service requires an active paid subscription. Fees are billed monthly in advance via credit or
              debit card through our third-party payment processor, Stripe. By subscribing you authorize us to charge your
              payment method on a recurring monthly basis.
            </Clause>
            <Clause title="2.2 Pricing.">
              Your monthly subscription fee is displayed at the time of purchase. We reserve the right to change pricing at
              any time. We will provide at least 30 days' advance notice of any price change by email. Your continued use of
              the Service after the price change takes effect constitutes acceptance of the new price.
            </Clause>
            <Clause title="2.3 Cancellation.">
              You may cancel your subscription at any time through your account settings or by contacting us at {PUBLIC_BUSINESS.email}.
              Cancellation takes effect at the end of your current billing period. You will retain access to the Service until
              the end of the period for which you have already paid.
            </Clause>
            <Clause title="2.4 No Refunds.">
              All subscription fees are non-refundable except as required by applicable law. We do not provide refunds or
              credits for partial months of service, unused features, or unused storage.
            </Clause>
            <Clause title="2.5 Failed Payments.">
              If a payment fails, we will attempt to retry the charge. If payment remains outstanding, your account may be
              suspended. You will be notified by email before any suspension.
            </Clause>
            <Clause title="2.6 Taxes.">
              You are responsible for all applicable taxes, duties, or charges imposed by any government authority in
              connection with your use of the Service.
            </Clause>
          </Section>

          <Section title="3. Acceptable Use">
            <Clause title="3.1 Permitted Use.">
              You may use the Service solely for your internal business operations related to managing your dealership's
              inventory, financing, and financial analytics.
            </Clause>
            <Clause title="3.2 Prohibited Conduct.">
              You agree not to:
              <ul className="mt-2 space-y-1 list-disc pl-5 text-muted-foreground">
                <li>Use the Service to transmit unlawful, fraudulent, or deceptive information</li>
                <li>Attempt to reverse-engineer, decompile, or disassemble any part of the Service</li>
                <li>Use the Service to process data on behalf of third parties for compensation without our written consent</li>
                <li>Share your account credentials with persons outside your organization</li>
                <li>Use automated means (bots, scrapers, crawlers) to access the Service</li>
                <li>Upload malicious code or attempt to interfere with the security or performance of the Service</li>
                <li>Violate any applicable law or regulation in connection with your use of the Service</li>
              </ul>
            </Clause>
            <Clause title="3.3 Content You Upload.">
              You retain all ownership rights to the financial documents, statements, and data you upload to the Service
              ("Your Data"). By uploading content you grant us a limited, non-exclusive license to process and store Your
              Data solely for the purpose of providing the Service to you.
            </Clause>
          </Section>

          <Section title="4. AI-Powered Features and Data Processing">
            <Clause title="4.1 AI Extraction.">
              The Service uses artificial intelligence models provided by third parties — including OpenAI and Anthropic —
              to extract and analyze data from documents you upload (such as lender statements, tax returns, and bank
              statements). By uploading documents you consent to their content being transmitted to these AI providers
              for the purpose of providing the Service.
            </Clause>
            <Clause title="4.2 Estimates Only — Dealer Must Verify All Data.">
              All figures, amounts, totals, and values extracted or generated by AI features of the Service are estimates
              produced by automated models and are not audited, certified, or guaranteed to be accurate. Extraction results,
              confidence scores, and processing statuses shown on the Platform do not constitute verified accounting and
              are not a substitute for review by a qualified professional. You are solely and exclusively responsible for
              reviewing, verifying, and correcting all AI-extracted, AI-generated, and platform-computed data before
              relying on it for any business, financial, operational, or regulatory decision. DealerMind accepts no
              liability for any loss, cost, or consequence arising from your reliance on unverified platform outputs.
            </Clause>
            <Clause title="4.3 Illustrative Analytics.">
              The Dealer Score, financial benchmarks, floorplan cost projections, turn-time estimates, holding-cost
              calculations, and all other analytics generated by the Service are illustrative tools designed to help you
              identify trends and patterns in your own data. They are not audited financial statements, certified
              calculations, regulatory filings, or professional opinions. Actual results may differ materially from any
              estimate or projection displayed on the Platform.
            </Clause>
            <Clause title="4.4 Accuracy Limitations.">
              AI-extracted data may contain errors, omissions, or misinterpretations resulting from document quality,
              formatting variations, or model limitations. DealerMind does not warrant that any calculation, output, or
              AI-generated insight is accurate, complete, or free from error.
            </Clause>
            <Clause title="4.5 No Professional Advice.">
              Nothing on the Platform constitutes financial, legal, tax, accounting, compliance, or investment advice.
              Platform outputs are informational tools only. DealerMind does not recommend specific lenders, financing
              products, business strategies, or operational decisions. You must engage qualified, licensed CPAs, attorneys,
              compliance consultants, and other professionals before making any significant business, financial, legal,
              or regulatory decision. Reliance on platform outputs as a substitute for qualified professional advice is
              entirely at your own risk.
            </Clause>
            <Clause title="4.6 Decisions Based on Platform Outputs.">
              DealerMind is never liable for any business decision, financing decision, vehicle purchase or sale,
              regulatory action, or other outcome that you make or take based on information, analytics, scores, or
              projections displayed on the Platform. The decision to act on any platform output rests solely with you.
            </Clause>
          </Section>

          <Section title="5. Dealer Marketplace">
            <Clause title="5.1 Neutral Listing Venue Only.">
              The Dealer Marketplace feature of the Service is a technology platform that allows dealers to create and
              view vehicle listings. DealerMind is a neutral venue only. We do not buy, sell, broker, inspect, appraise,
              title, or take possession of any vehicle. We are not a party to any transaction between dealers.
            </Clause>
            <Clause title="5.2 Dealer Responsibility for Listings.">
              You are solely responsible for the accuracy, completeness, and legality of all listing content you submit,
              including but not limited to vehicle descriptions, photographs, pricing, condition disclosures, mileage,
              title status, lien status, and any other representations. DealerMind does not verify, inspect, or
              independently confirm the accuracy of any listing.
            </Clause>
            <Clause title="5.3 No Warranties on Listings.">
              DealerMind makes no representations or warranties of any kind regarding the accuracy, condition, title
              status, or fitness of any vehicle listed on the Marketplace. All listings are provided by dealers and
              DealerMind expressly disclaims any and all warranties, express or implied, with respect to any vehicle
              or listing content.
            </Clause>
            <Clause title="5.4 Transactions Are Between Dealers.">
              All negotiations, deal terms, pricing, representations, condition disclosures, title transfers, inspections,
              payments, financing arrangements, and disputes are exclusively between the transacting dealers. DealerMind
              provides no payment processing, escrow, title services, or dispute resolution for marketplace transactions.
              DealerMind bears zero liability for any deal, loss, damage, fraud, misrepresentation, failed transaction,
              title defect, undisclosed lien, or any other outcome arising from or related to a marketplace transaction.
            </Clause>
            <Clause title="5.5 No Liability for Marketplace Losses.">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, DEALERMIND SHALL NOT BE LIABLE FOR ANY VEHICLE LOSS,
              DEAL LOSS, FINANCIAL LOSS, FRAUD LOSS, OR ANY OTHER DAMAGE OF ANY NATURE ARISING FROM OR RELATED TO YOUR
              USE OF THE DEALER MARKETPLACE, INCLUDING LOSSES CAUSED BY INACCURATE LISTINGS, MISREPRESENTATIONS BY
              OTHER DEALERS, FAILED PAYMENTS, TITLE DEFECTS, OR VEHICLE CONDITION ISSUES.
            </Clause>
            <Clause title="5.6 Prohibited Marketplace Conduct.">
              You agree not to submit listings containing: false or misleading information; vehicles with known undisclosed
              title defects or liens; stolen vehicles; or any content that violates applicable law. Violation of this
              clause is grounds for immediate account termination and you may be held liable for any resulting losses.
            </Clause>
          </Section>

          <Section title="6. Third-Party Services">
            <Clause title="6.1 Reliance on Third-Party Providers.">
              The Service relies on third-party technology providers to function, including but not limited to: Stripe
              (payment processing), OpenAI (AI language models), Anthropic (AI language models), Neon (database hosting),
              Resend (email delivery), Mercury (ACH payment processing), Twilio (SMS delivery), and Replit (cloud infrastructure). DealerMind
              does not control these providers and their availability, accuracy, and reliability are subject to their
              own terms of service and operating conditions.
            </Clause>
            <Clause title="6.2 No Liability for Third-Party Failures.">
              DealerMind is not liable for any service interruption, data loss, processing error, incorrect output,
              security incident, outage, or other failure caused by or attributable to any third-party provider.
              This includes, without limitation: failed or delayed payment processing by Stripe; inaccurate or
              incomplete AI outputs from OpenAI or Anthropic; database outages or data loss at Neon; failed or
              delayed email delivery by Resend; failed or delayed ACH payouts by Mercury; or infrastructure
              failures at Replit. Your recourse for third-party failures lies with those providers under their
              respective terms and policies.
            </Clause>
            <Clause title="6.3 Third-Party Data Processing.">
              When you use AI-powered features, your document content is transmitted to and processed by OpenAI and/or
              Anthropic subject to their privacy policies and data processing agreements. DealerMind is not responsible
              for how these providers store, process, or handle data transmitted to them as part of the Service.
            </Clause>
          </Section>

          <Section title="7. Email Alerts">
            <Clause title="7.1 Best-Effort Delivery.">
              The Service includes optional email alert features such as aged-vehicle notifications, holding-cost
              alerts, and daily digest summaries. These alerts are provided as a convenience feature on a best-effort
              basis. Delivery of alerts depends on third-party email infrastructure (including Resend), your email
              provider's filtering and spam settings, and network conditions — none of which DealerMind controls.
            </Clause>
            <Clause title="7.2 No Guarantee of Delivery.">
              DealerMind does not guarantee that any alert will be delivered, delivered on time, or delivered in
              any particular format. Alerts may be missed, delayed, filtered as spam, or fail to send due to
              technical issues beyond our control.
            </Clause>
            <Clause title="7.3 No Reliance on Alerts for Operational Decisions.">
              You must not rely solely on email alerts for operational, financial, or business-critical decisions.
              You are responsible for independently monitoring your inventory, holding costs, floorplan balances,
              and other operational metrics through the Platform itself. DealerMind is not liable for any loss,
              cost, or damage arising from a missed, delayed, or failed alert.
            </Clause>
          </Section>

          <Section title="8. Text Message (SMS) Alerts">
            <Clause title="8.1 Program.">
              DealerMind SMS Alerts sends lead and account alerts by text to dealership staff who use DealerHub,
              DealerMind's dealership app. We do not send marketing texts through this program and do not text
              car shoppers through it.
            </Clause>
            <Clause title="8.2 How you opt in.">
              You opt in in DealerHub under Settings → Notifications by entering a mobile number and checking
              the SMS consent box. Consent is not a condition of purchase.
            </Clause>
            <Clause title="8.3 Frequency and cost.">
              Message frequency varies with your dealership's lead activity. Message and data rates may apply.
            </Clause>
            <Clause title="8.4 Opting out and help.">
              Reply STOP to any message to stop receiving texts; you'll get one confirmation. Reply HELP for help,
              or email {PUBLIC_BUSINESS.email}. You can also remove your number in DealerHub settings.
            </Clause>
            <Clause title="8.5 Carriers.">
              Wireless carriers are not liable for delayed or undelivered messages.
            </Clause>
            <Clause title="8.6 Privacy.">
              Mobile numbers and SMS consent information will not be shared with third parties or affiliates
              for marketing or promotional purposes. See our <Link href="/privacy" className="underline">Privacy Policy</Link>.
            </Clause>
          </Section>

          <Section title="9. Intellectual Property">
            <Clause title="9.1 Our IP.">
              DealerMind and its licensors own all right, title, and interest in and to the Service, including all
              software, algorithms, designs, trademarks, and content (excluding Your Data). These Terms do not grant
              you any ownership rights in the Service.
            </Clause>
            <Clause title="9.2 Your Data.">
              You own Your Data. We do not claim ownership of any documents or data you upload.
            </Clause>
            <Clause title="9.3 Feedback.">
              If you provide suggestions, feedback, or ideas about the Service, you grant us a perpetual, irrevocable,
              royalty-free license to use and incorporate that feedback without any obligation to you.
            </Clause>
          </Section>

          <Section title="10. Confidentiality and Data Security">
            <Clause title="10.1 Security Measures.">
              We implement industry-standard technical and organizational measures to protect Your Data, including
              encrypted storage, secure HTTPS transmission, and access controls. However, no system is completely
              secure and we cannot guarantee absolute security.
            </Clause>
            <Clause title="10.2 Data Breach.">
              In the event of a data breach that affects Your Data, we will notify you as required by applicable law.
            </Clause>
          </Section>

          <Section title="11. Limitation of Liability">
            <Clause title="11.1 Disclaimer of Warranties.">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTY OF ANY KIND, EXPRESS OR IMPLIED.
              WE EXPRESSLY DISCLAIM ALL WARRANTIES INCLUDING, WITHOUT LIMITATION, IMPLIED WARRANTIES OF MERCHANTABILITY,
              FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </Clause>
            <Clause title="11.2 Excluded Damages.">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL DEALERMIND, ITS OFFICERS, DIRECTORS,
              EMPLOYEES, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR
              PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, GOODWILL, OR BUSINESS OPPORTUNITIES, ARISING
              FROM OR RELATED TO YOUR USE OF OR INABILITY TO USE THE SERVICE, EVEN IF ADVISED OF THE POSSIBILITY OF
              SUCH DAMAGES. WITHOUT LIMITING THE FOREGOING, THIS EXCLUSION EXPRESSLY COVERS:
              <ul className="mt-2 space-y-1 list-disc pl-5">
                <li>Vehicle losses, inventory write-downs, or depreciation losses</li>
                <li>Deal losses arising from marketplace transactions or pricing decisions</li>
                <li>Losses from floorplan financing decisions made based on platform outputs</li>
                <li>Failed or unfavorable financing outcomes</li>
                <li>Regulatory fines, penalties, or compliance violations</li>
                <li>Losses arising from reliance on AI-generated outputs, Dealer Score, benchmarks, or projections</li>
                <li>Losses arising from marketplace listing misrepresentations by other dealers</li>
                <li>Losses caused by third-party provider outages, errors, or data processing failures</li>
                <li>Losses from missed, delayed, or failed email alerts</li>
                <li>Any loss arising from a dealer decision made based on platform outputs</li>
              </ul>
            </Clause>
            <Clause title="11.3 Cap on Liability.">
              OUR TOTAL AGGREGATE LIABILITY TO YOU FOR ANY CLAIM ARISING FROM OR RELATED TO THESE TERMS OR THE SERVICE
              SHALL NOT EXCEED THE TOTAL AMOUNT PAID BY YOU FOR THE SERVICE IN THE ONE (1) MONTH IMMEDIATELY PRECEDING
              THE EVENT GIVING RISE TO THE CLAIM.
            </Clause>
            <Clause title="11.4 Essential Basis.">
              THE LIMITATIONS IN THIS SECTION APPLY TO ALL THEORIES OF LIABILITY (TORT, CONTRACT, STRICT LIABILITY,
              OR OTHER) AND REFLECT AN ALLOCATION OF RISK THAT IS AN ESSENTIAL BASIS OF THE BARGAIN BETWEEN THE PARTIES.
              DEALERMIND WOULD NOT PROVIDE THE SERVICE WITHOUT THESE LIMITATIONS.
            </Clause>
          </Section>

          <Section title="12. Indemnification">
            <p className="text-muted-foreground leading-relaxed">
              You agree to defend, indemnify, and hold harmless DealerMind and its officers, directors, employees, and
              agents from any claims, damages, losses, liabilities, and expenses (including reasonable attorneys' fees)
              arising from: (a) your use of the Service; (b) Your Data; (c) your violation of these Terms; (d) your
              violation of any applicable law or the rights of any third party; (e) any vehicle listing you submit to
              the Dealer Marketplace, including any misrepresentation, condition defect, title defect, or other issue
              relating to a listed vehicle or any resulting transaction; (f) any dispute between you and another dealer
              arising from a marketplace transaction; or (g) any third-party claim arising from content, data, or
              representations you submit through the Platform.
            </p>
          </Section>

          <Section title="13. Account Suspension and Termination">
            <Clause title="13.1 Termination by You.">
              You may stop using the Service and cancel your subscription at any time. Cancellation takes effect at the
              end of the current billing period.
            </Clause>
            <Clause title="13.2 Termination by Us.">
              We may suspend or terminate your account immediately and without notice if: (a) you breach these Terms;
              (b) we determine your use poses a risk to the Service or other users; (c) you fail to pay applicable fees;
              or (d) required by law.
            </Clause>
            <Clause title="13.3 Effect of Termination.">
              Upon termination, your right to access the Service ceases. Your Data will be retained for 30 days after
              termination, after which it may be permanently deleted. You may request an export of Your Data within
              this window by contacting {PUBLIC_BUSINESS.email}.
            </Clause>
          </Section>

          <Section title="14. Dispute Resolution and Governing Law">
            <Clause title="14.1 Governing Law.">
              These Terms shall be governed by and construed in accordance with the laws of the State of Florida,
              without regard to its conflict of law provisions.
            </Clause>
            <Clause title="14.2 Disputes.">
              Any dispute arising out of or relating to these Terms or the Service shall first be submitted to
              good-faith negotiation. If unresolved within 30 days, disputes shall be resolved by binding arbitration
              administered by the American Arbitration Association (AAA) under its then-current rules, except that either party may seek
              injunctive relief in a court of competent jurisdiction.
            </Clause>
            <Clause title="14.3 Class Action Waiver.">
              YOU AGREE THAT ANY DISPUTE RESOLUTION PROCEEDINGS WILL BE CONDUCTED ONLY ON AN INDIVIDUAL BASIS AND
              NOT IN A CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION.
            </Clause>
          </Section>

          <Section title="15. Modifications to Terms">
            <p className="text-muted-foreground leading-relaxed">
              We may modify these Terms at any time. We will provide at least 14 days' notice before material changes
              take effect by emailing the address on your account. Your continued use of the Service after the
              effective date constitutes acceptance of the revised Terms. If you do not agree to the new Terms, you
              must cancel your subscription before the effective date.
            </p>
          </Section>

          <Section title="16. Miscellaneous">
            <Clause title="16.1 Entire Agreement.">
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and DealerMind
              regarding the Service and supersede all prior agreements.
            </Clause>
            <Clause title="16.2 Severability.">
              If any provision of these Terms is found unenforceable, the remaining provisions remain in full force
              and effect.
            </Clause>
            <Clause title="16.3 Waiver.">
              Failure to enforce any provision of these Terms shall not constitute a waiver of our right to enforce
              it in the future.
            </Clause>
            <Clause title="16.4 Assignment.">
              You may not assign these Terms without our prior written consent. We may assign these Terms freely.
            </Clause>
          </Section>

          <div className="mt-12 pt-8 border-t text-sm text-muted-foreground">
            <p>
              <strong className="text-foreground">Contact:</strong>{" "}
              {PUBLIC_BUSINESS.email} | DealerMind Intelligence LLC | {PUBLIC_BUSINESS.mailingAddress}
            </p>
          </div>
        </div>
      </main>

      <footer className="border-t mt-12">
        <div className="max-w-4xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <span>&copy; 2026 DealerMind. All rights reserved.</span>
          <div className="flex items-center gap-4">
            <Link href="/terms" className="hover:text-foreground transition-colors" data-testid="footer-link-terms">
              Terms of Service
            </Link>
            <Link href="/privacy" className="hover:text-foreground transition-colors" data-testid="footer-link-privacy">
              Privacy Policy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-10">
      <h2 className="text-xl font-semibold text-foreground mb-4 pb-2 border-b">{title}</h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Clause({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-foreground font-medium mb-1">{title}</p>
      <div className="text-muted-foreground leading-relaxed">{children}</div>
    </div>
  );
}
